import React, { useEffect, useState } from "react";
import { shade, tint } from "polished";
import styled, { css, keyframes } from "styled-components";
import { BrowserRouter, Link, Switch, Route } from "react-router-dom";
import { Paintings } from "./pages/paintingsGrid";
import { OtherWork } from "./pages/otherwork";
import { DesignWork } from "./pages/design";
import { Contact } from "./pages/contact";

import "./reset.css";
import "./global.css";
import * as Clrs from "./js/colors";

const mainAreaBg = tint(0.9, Clrs.basegrey);
const darkRailBg = shade(0.4, Clrs.basegrey);
const railTitlePrimaryColor = tint(0.6, Clrs.basegrey);
const railTextColor = tint(0.8, Clrs.basegrey);
const BG_FADE_MS = 1600;
const BG_OPACITY = 0.54;

const fadeInBackground = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: ${BG_OPACITY};
  }
`;

const AppShell = styled.div`
  display: flex;
  min-height: 100vh;
  background-color: ${mainAreaBg};

  @media (max-width: 980px) {
    flex-direction: column;
    min-height: 100vh;
  }
`;

const LeftRail = styled.aside`
  position: relative; /* Ensuring position is set */
  z-index: 2;
  width: 320px;
  min-height: 100vh;
  padding: 42px 28px;
  box-sizing: border-box;
  background-color: ${darkRailBg};
  border-right: 1px solid ${Clrs.white};

  &::after {
    content: "";
    position: absolute;
    top: 0;
    bottom: 0;
    right: -8px;
    width: 8px;
    z-index: 3;
    pointer-events: none;
    background: linear-gradient(
      to right,
      rgba(0, 0, 0, 0.3) 0%,
      rgba(0, 0, 0, 0.2) 35%,
      rgba(0, 0, 0, 0.1) 68%,
      rgba(0, 0, 0, 0) 100%
    );
  }

  @media (max-width: 980px) {
    width: 100%;
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid ${Clrs.white};
    padding: 24px 16px;
  }
`;

const RailTitle = styled.h1`
  margin: 0;
  color: ${railTextColor};
  font-weight: 900;
  line-height: 0.9;
  letter-spacing: 0.02em;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
`;

const RailTitlePrimary = styled.span`
  display: block;
  color: ${railTitlePrimaryColor};
  font-size: 55px;
  font-weight: 900;
  line-height: 0.95;
  letter-spacing: -0.02em;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;

  @media (max-width: 980px) {
    font-size: 40px;
    line-height: 1;
  }
`;

const RailTitleSecondary = styled.span`
  display: block;
  margin-top: 0;
  font-size: 22px;
  font-weight: 500;
  line-height: 1.1;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;

  @media (max-width: 980px) {
    font-size: 18px;
    margin-top: 2px;
  }
`;

const RailBio = styled.p`
  margin: 14px 0 0;
  color: ${railTextColor};
  opacity: 0.86;
  font-size: 12px;
  line-height: 1.65;
  font-family: Helvetica, Arial, sans-serif;

  @media (max-width: 980px) {
    margin-top: 18px;
    font-size: 10px;
    line-height: 1.55;
    max-width: 72ch;
  }
`;

const RightPane = styled.main`
  position: relative;
  z-index: 1;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 0;
  min-height: 100vh;
  background-color: ${mainAreaBg};

  @media (max-width: 980px) {
    width: 100%;
    min-height: auto;
  }
`;

const MainContentArea = styled.div`
  position: relative;
  flex: 1;
  height: 100%;
  min-height: 100vh;
  background-color: ${mainAreaBg};
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 20px;
  box-sizing: border-box;
  z-index: 20;

  @media (max-width: 980px) {
    height: 100%;
    min-height: 100vh;
    overflow: hidden;
    padding: 12px;
  }
`;

const ContentBackground = styled.div`
  position: absolute;
  inset: -48px;
  background-image: ${(props) => (props.image ? `url(${props.image})` : "none")};
  background-size: cover;
  background-position: center;
  filter: blur(7px);
  opacity: ${(props) => (props.visible ? BG_OPACITY : 0)};
  transform: scale(1.08);
  transition: opacity ${BG_FADE_MS}ms ease-in-out;
  animation: ${(props) =>
    props.animateIn
      ? css`
          ${fadeInBackground} ${BG_FADE_MS}ms ease-in-out
        `
      : "none"};
  pointer-events: none;
`;

const ContentBackgroundWash = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(247, 249, 250, 0.28);
  pointer-events: none;
`;

const ContentLayer = styled.div`
  position: relative;
  z-index: 1;
  flex: 1;
  height: 100%;
  min-height: 100%;
  overflow-x: hidden;
  overflow-y: auto;

  @media (max-width: 980px) {
    height: 100%;
    min-height: 100%;
    overflow: visible;
  }
`;

function App() {
  const [activeBackground, setActiveBackground] = useState(null);
  const [previousBackground, setPreviousBackground] = useState(null);
  const [previousVisible, setPreviousVisible] = useState(false);
  const [activeBackgroundKey, setActiveBackgroundKey] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (!previousBackground) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setPreviousBackground(null);
      setPreviousVisible(false);
    }, BG_FADE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [previousBackground]);

  function handlePaintingBackgroundChange(nextImage) {
    setActiveBackground((currentImage) => {
      if (currentImage === nextImage) {
        return currentImage;
      }

      if (currentImage) {
        setPreviousBackground(currentImage);
        setPreviousVisible(true);
        window.requestAnimationFrame(() => {
          setPreviousVisible(false);
        });
      }

      setActiveBackgroundKey((key) => key + 1);
      return nextImage;
    });
  }

  return (
    <>
      <BrowserRouter>
        <AppShell>
          <LeftRail>
            <MenuButton
              type="button"
              aria-label="Open navigation menu"
              aria-expanded={menuOpen ? "true" : "false"}
              onClick={() => setMenuOpen((value) => !value)}
            >
              <MenuIcon />
            </MenuButton>

            <MenuPanel $open={menuOpen} aria-hidden={menuOpen ? "false" : "true"}>
              <MenuPanelInner $open={menuOpen}>
                <MenuLink to="/paintings" onClick={() => setMenuOpen(false)}>
                  Portfolio
                </MenuLink>
                <MenuLink to="/contact" onClick={() => setMenuOpen(false)}>
                  Contact
                </MenuLink>
              </MenuPanelInner>
            </MenuPanel>

            <RailTitle>
              <RailTitlePrimary>J. Price</RailTitlePrimary>
              <RailTitleSecondary>Selected Artworks</RailTitleSecondary>
            </RailTitle>
            <RailBio>
              Jason Price is an oil painter whose work typically explores the blurred
              boundary between the natural world and human intervention. Drawing
              from imagined landscapes and abstract forms, his paintings
              challenge conventional perceptions of nature, suggesting that what
              we often accept as "natural" is, in fact, shaped, subtly or
              overtly, by human hands.
              <br />
              <br />
              Originally trained in Graphic Design at Drexel University, Jason
              brings a sense of composition and visual structure to his painting
              practice. His work is rooted in a fascination with landscapes
              altered by human presence, whether through infrastructure,
              agriculture, or industry, rendering scenes that are both familiar
              and a bit surreal.
            </RailBio>
          </LeftRail>

          <RightPane>
            <MainContentArea>
              <ContentBackground image={previousBackground} visible={previousVisible} />
              <ContentBackground
                key={`active-bg-${activeBackgroundKey}`}
                image={activeBackground}
                visible={Boolean(activeBackground)}
                animateIn={Boolean(activeBackground)}
              />
              <ContentBackgroundWash />
              <ContentLayer>
                <Switch>
                  {/* <Route path="/about">
                    <About />
                  </Route> */}
                  <Route path="/otherwork">
                    <OtherWork />
                  </Route>
                  <Route path="/design">
                    <DesignWork />
                  </Route>
                  <Route path="/contact">
                    <Contact />
                  </Route>
                  <Route path="/paintings">
                    <Paintings onBackgroundChange={handlePaintingBackgroundChange} />
                  </Route>
                  <Route path="/">
                    <Paintings onBackgroundChange={handlePaintingBackgroundChange} />
                  </Route>
                </Switch>
              </ContentLayer>
            </MainContentArea>
          </RightPane>
        </AppShell>
      </BrowserRouter>
    </>
  );
}

const MenuButton = styled.button`
  position: absolute;
  top: 3px;
  right: 6px;
  width: 34px;
  height: 34px;
  border: none;
  padding: 0;
  background: transparent;
  cursor: pointer;
  display: grid;
  place-items: center;

  &:focus-visible {
    outline: 2px solid ${railTextColor};
    outline-offset: 2px;
  }
`;

const MenuIcon = styled.span`
  position: relative;
  width: 18px;
  height: 1.5px;
  background: ${railTextColor};
  display: block;

  &::before,
  &::after {
    content: "";
    position: absolute;
    left: 0;
    width: 18px;
    height: 1.5px;
    background: ${railTextColor};
  }

  &::before {
    top: -6px;
  }

  &::after {
    top: 6px;
  }
`;

const MenuPanel = styled.nav`
  position: relative;
  width: calc(100% + 56px);
  margin: 0 -28px ${(props) => (props.$open ? "18px" : "0")};
  padding: 0 28px;
  box-sizing: border-box;
  background: ${(props) => (props.$open ? railTitlePrimaryColor : "transparent")};
  border-top: 1px solid ${(props) => (props.$open ? tint(0.66, Clrs.basegrey) : "transparent")};
  border-bottom: 1px solid ${(props) => (props.$open ? tint(0.66, Clrs.basegrey) : "transparent")};
  max-height: ${(props) => (props.$open ? "130px" : "0")};
  overflow: hidden;
  z-index: 1;
  transition:
    max-height 320ms ease,
    margin-bottom 320ms ease,
    padding 320ms ease,
    background-color 220ms ease,
    border-color 220ms ease;
  padding-top: ${(props) => (props.$open ? "12px" : "0")};
  padding-bottom: ${(props) => (props.$open ? "12px" : "0")};

  @media (max-width: 980px) {
    width: calc(100% + 32px);
    margin: ${(props) => (props.$open ? "30px" : "0")} -16px
      ${(props) => (props.$open ? "14px" : "0")};
    padding-left: 16px;
    padding-right: 16px;
    padding-top: ${(props) => (props.$open ? "10px" : "0")};
    padding-bottom: ${(props) => (props.$open ? "10px" : "0")};
  }
`;

const MenuPanelInner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  opacity: ${(props) => (props.$open ? 1 : 0)};
  transform: translateY(${(props) => (props.$open ? "0" : "-6px")});
  transition: opacity 170ms ease, transform 170ms ease;
  transition-delay: ${(props) => (props.$open ? "170ms" : "0ms")};
  pointer-events: ${(props) => (props.$open ? "auto" : "none")};
`;

const MenuLink = styled(Link)`
  color: ${darkRailBg};
  text-decoration: none;
  font-family: "Helvetica Neue", Helvetica, Arial, sans-serif;
  font-size: 14px;
  font-weight: 800;
  letter-spacing: 0.01em;

  &:hover {
    text-decoration: none;
    opacity: 0.82;
  }
`;

export default App;

/* function About() {
  return (
    <P>
      These are the personal artworks of J. Price. A heavy dose of landscape -
      mostly imagined - and often an exploration into how humanity alters it.
    </P>
  );
} */

/* function OtherWork() {
    return(<p>test other</p>)
}
 */
